import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  useTheme,
  useMediaQuery,
  Divider,
  Fade,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Lock as LockIcon,
  Notifications as NotificationsIcon,
  Payment as PaymentIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  VpnKey as KeyIcon,
  AttachMoney as MoneyIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import PasswordTab from './tabs/PasswordTab';
import NotificationsTab from './tabs/NotificationsTab';
import PaymentMethodsTab from './tabs/PaymentMethodsTab';
import AdminPaymentMethodsTab from './tabs/AdminPaymentMethodsTab';
import PlatformFeesTab from './tabs/PlatformFeesTab';
import AddressesTab from './tabs/AddressesTab';
import ApiKeysTab from './tabs/ApiKeysTab';
import ProfileTab from './tabs/ProfileTab';
import { Address } from '../../services/addressService';
import { useTranslation } from '../../contexts/TranslationContext';
import { useAppMode } from '../../contexts/AppModeContext';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  initialTab?: number;
  prefilledAddress?: Partial<Address>;
  onAddressAdded?: (address: Address) => void;
  readonlyFields?: boolean;
  coordinates?: { lat: number; lng: number };
  isAdmin?: boolean; // If true, shows platform settings instead of user settings
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </Box>
      )}
    </div>
  );
}

const SettingsModal: React.FC<SettingsModalProps> = ({ open, onClose, initialTab = 0, prefilledAddress, onAddressAdded, readonlyFields = false, coordinates, isAdmin = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(initialTab);
  const { t } = useTranslation();
  const { mode } = useAppMode();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Update activeTab when initialTab prop changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const tabs = [
    {
      label: t('settings.tabs.profile'),
      icon: <PersonIcon />,
      component: <ProfileTab />,
    },
    {
      label: t('settings.tabs.password'),
      icon: <LockIcon />,
      component: <PasswordTab />,
    },
    {
      label: t('settings.tabs.notifications'),
      icon: <NotificationsIcon />,
      component: <NotificationsTab />,
    },
    {
      label: isAdmin ? t('settings.tabs.platformPaymentMethods') : t('settings.tabs.paymentMethods'),
      icon: <PaymentIcon />,
      component: isAdmin ? <AdminPaymentMethodsTab /> : <PaymentMethodsTab />,
    },
    ...(isAdmin ? [{
      label: t('settings.tabs.platformFees'),
      icon: <MoneyIcon />,
      component: <PlatformFeesTab />,
    }] : []),
    {
      label: t('settings.tabs.addresses'),
      icon: <LocationIcon />,
      component: <AddressesTab prefilledAddress={prefilledAddress} onAddressAdded={onAddressAdded} readonlyFields={readonlyFields} coordinates={coordinates} />,
    },
    ...(mode !== 'shop' ? [{
      label: t('settings.tabs.apiKeys'),
      icon: <KeyIcon />,
      component: <ApiKeysTab />,
    }] : []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '90vh',
          background: '#ffffff',
          border: '1px solid rgba(0, 0, 0, 0.06)',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
          overflow: 'hidden',
        },
      }}
    >
      {!isMobile && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            pb: 2,
            background: '#ffffff',
            borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary' }}>
            {isAdmin ? t('settings.title.platform') : t('settings.title.user')}
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.05)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
      )}

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            minHeight: '60vh',
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          {/* Navigation */}
          {isMobile ? (
            <Box sx={{ position: 'sticky', top: 0, zIndex: 4, p: 1.5, pt: 1, pb: 1, width: '100%', borderBottom: '1px solid rgba(0,0,0,0.06)', background: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
              <FormControl fullWidth size="small" sx={{ flexGrow: 1 }}>
                <InputLabel id="settings-mobile-select">{tabs[activeTab]?.label}</InputLabel>
                <Select
                  labelId="settings-mobile-select"
                  value={activeTab}
                  label={tabs[activeTab]?.label}
                  onChange={(e) => setActiveTab(Number(e.target.value))}
                  sx={{ borderRadius: 2, '& .MuiSelect-select': { py: 1 } }}
                >
                  {tabs.map((tab, index) => (
                    <MenuItem key={index} value={index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        {tab.icon}
                        <Typography>{tab.label}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconButton onClick={onClose} sx={{ ml: 0.5 }} aria-label="Close">
                <CloseIcon />
              </IconButton>
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                width: 280,
                minWidth: 280,
                background: '#fafafa',
                borderRight: '1px solid rgba(0, 0, 0, 0.05)',
                borderRadius: 0,
                overflow: 'hidden',
              }}
            >
              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                variant="standard"
                sx={{
                  '& .MuiTabs-indicator': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 2,
                  },
                  '& .MuiTab-root': {
                    minHeight: 60,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      color: '#667eea',
                      fontWeight: 600,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(102, 126, 234, 0.08)',
                    },
                  },
                }}
              >
                {tabs.map((tab, index) => (
                  <Tab
                    key={index}
                    icon={tab.icon}
                    label={tab.label}
                    iconPosition="start"
                    sx={{ justifyContent: 'flex-start', px: 3 }}
                  />
                ))}
              </Tabs>
            </Paper>
          )}

          {/* Content Area */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            {tabs.map((tab, index) => (
              <TabPanel key={index} value={activeTab} index={index}>
                <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 1, md: 2 }, overflowY: 'auto', maxHeight: { xs: 'calc(100vh - 140px)', md: 'unset' } }}>
                  {tab.component}
                </Box>
              </TabPanel>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default SettingsModal;
